/**
 * Roadmaps — the Journey and Learning surfaces of the five-area shell.
 *
 * PRESENTATION ONLY. Nothing here scores, ranks or infers anything. It takes
 * two facts that the canonical engine already produced — the learner's primary
 * archetype and their age band — and composes a human, doable sequence of
 * steps out of hand-written copy. No psychometrics, no career data, no maths.
 *
 * Composition: a band spine (what a step means at this age) crossed with an
 * archetype flavour (what that step looks like for this person). Deterministic:
 * same archetype + band always yields the same roadmap.
 */
import { ARCHETYPES, type Archetype } from "@/features/assessment-v2/archetypes";
import type { Band } from "@/features/assessment-v2/band";

export type RoadmapKind = "journey" | "learning";

export type ActionKind = "learn" | "build" | "talk" | "reflect";

export interface RoadmapAction {
  label: string;
  kind: ActionKind;
  /** Rough time to do it once, in minutes. Presentation only. */
  minutes: number;
}

export interface Milestone {
  id: string;
  /** "Step 1 · This month" — the when. */
  phase: string;
  title: string;
  /** One warm sentence on why this step is worth it. */
  why: string;
  actions: RoadmapAction[];
}

export interface Roadmap {
  kind: RoadmapKind;
  archetype: Archetype;
  band: Band;
  title: string;
  intro: string;
  milestones: Milestone[];
}

// ---- archetype flavour ---------------------------------------------------

interface Flavour {
  /** How this person naturally moves. */
  verb: string;
  /** The craft word we use for their practice. */
  craft: string;
  /** Something small they can make. */
  build: string;
  /** Someone worth talking to. */
  talk: string;
  /** A first thing to learn. */
  learn: string;
  /** A deeper thing to learn once the first lands. */
  deepen: string;
  /** Proof they can show someone. */
  proof: string;
}

const FLAVOUR: Record<Archetype, Flavour> = {
  Builder: {
    verb: "make it real",
    craft: "hands-on making",
    build: "fix or improve one thing that annoys you at home",
    talk: "someone who builds, repairs or installs for a living",
    learn: "the tools and safety basics of one trade or workshop",
    deepen: "how a whole system fits together, not just your part",
    proof: "photos of something you built, before and after",
  },
  Thinker: {
    verb: "get to the bottom of it",
    craft: "careful reasoning",
    build: "a short written breakdown of a problem nobody has explained well",
    talk: "someone who researches or analyses for a living",
    learn: "how to read a dataset or a paper without getting lost",
    deepen: "one method properly — statistics, logic or a modelling tool",
    proof: "a two-page analysis with your own conclusion",
  },
  Creator: {
    verb: "give it a shape",
    craft: "making things people feel",
    build: "one finished piece a week, however small",
    talk: "someone whose creative work you actually admire",
    learn: "the fundamentals of your medium — composition, rhythm, structure",
    deepen: "the craft behind the craft: editing, iteration, critique",
    proof: "a portfolio of five pieces you'd defend",
  },
  Connector: {
    verb: "bring people in",
    craft: "building trust quickly",
    build: "introduce two people who should know each other",
    talk: "someone who works with clients, partners or communities",
    learn: "how to ask questions that open a conversation",
    deepen: "negotiation and follow-through — the unglamorous half",
    proof: "three people who'd vouch for you by name",
  },
  Organizer: {
    verb: "get it running smoothly",
    craft: "making order out of mess",
    build: "a simple system for something chaotic in your week",
    talk: "someone who runs operations, logistics or events",
    learn: "one planning tool well enough to teach it",
    deepen: "how to spot the bottleneck before it bites",
    proof: "a plan you ran that finished on time",
  },
  Leader: {
    verb: "take it on",
    craft: "carrying responsibility",
    build: "lead one small thing end to end, even a group of two",
    talk: "someone who leads a team and will be honest about it",
    learn: "how to set a goal others can actually follow",
    deepen: "giving feedback that lands without wounding",
    proof: "something that happened because you decided it would",
  },
  Caregiver: {
    verb: "look after people",
    craft: "steady attention to people",
    build: "commit to one hour a week supporting someone specific",
    talk: "someone in care, teaching or health who has lasted",
    learn: "listening and boundaries — both, together",
    deepen: "the practical side: first aid, safeguarding, note-keeping",
    proof: "a reference from someone you supported",
  },
  Advocate: {
    verb: "stand up for it",
    craft: "arguing for what matters",
    build: "write the case for one change you want to see",
    talk: "someone who campaigns, mediates or does policy work",
    learn: "how to find and check evidence for a claim",
    deepen: "how change actually happens where you live",
    proof: "one thing that changed because you pushed",
  },
};

// ---- band spine ---------------------------------------------------------

interface Phase {
  phase: string;
  title: (f: Flavour) => string;
  why: (f: Flavour) => string;
  actions: (f: Flavour) => RoadmapAction[];
}

const JOURNEY_SPINE: Record<Band, Phase[]> = {
  "18plus": [
    {
      phase: "Step 1 · This month",
      title: (f) => `Test the ground — ${f.verb}`,
      why: () => "Before you commit anything big, spend four weeks finding out whether the day-to-day suits you.",
      actions: (f) => [
        { label: `Talk to ${f.talk} for twenty minutes`, kind: "talk", minutes: 20 },
        { label: `Start this: ${f.build}`, kind: "build", minutes: 90 },
        { label: "Write down what you'd happily do again", kind: "reflect", minutes: 10 },
      ],
    },
    {
      phase: "Step 2 · Months 2–3",
      title: (f) => `Build one visible piece of ${f.craft}`,
      why: () => "One finished thing beats five started ones. It is also the fastest way to be taken seriously.",
      actions: (f) => [
        { label: `Learn ${f.learn}`, kind: "learn", minutes: 240 },
        { label: `Put together ${f.proof}`, kind: "build", minutes: 300 },
        { label: "Ask one person to pick it apart", kind: "talk", minutes: 30 },
      ],
    },
    {
      phase: "Step 3 · Months 4–6",
      title: () => "Get paid or get trusted",
      why: () => "A first paid piece of work, an internship or a real responsibility — proof that someone bet on you.",
      actions: (f) => [
        { label: "Apply to five real openings, cheapest route in first", kind: "build", minutes: 120 },
        { label: `Deepen it: ${f.deepen}`, kind: "learn", minutes: 300 },
        { label: "Ask two people to keep an eye out for you", kind: "talk", minutes: 20 },
      ],
    },
    {
      phase: "Step 4 · Months 7–12",
      title: () => "Widen the circle",
      why: () => "Most doors open sideways. The people who already do this work are the door.",
      actions: (f) => [
        { label: `Join one group or guild where ${f.craft} is normal`, kind: "talk", minutes: 60 },
        { label: "Teach someone the thing you just learned", kind: "talk", minutes: 45 },
        { label: "Update your evidence with what changed", kind: "reflect", minutes: 30 },
      ],
    },
    {
      phase: "Step 5 · Year 2",
      title: () => "Choose your next depth",
      why: () => "Now you have data on yourself. Re-read your Career DNA™ and pick the harder path on purpose.",
      actions: () => [
        { label: "Re-take the walk and compare the two", kind: "reflect", minutes: 15 },
        { label: "Pick one qualification or role worth two years", kind: "learn", minutes: 60 },
        { label: "Say no to one thing to make room", kind: "reflect", minutes: 10 },
      ],
    },
  ],
  "15to18": [
    {
      phase: "Step 1 · This month",
      title: (f) => `Try it before you choose it — ${f.verb}`,
      why: () => "Subject choices feel final. They are much less final if you have actually tried the work.",
      actions: (f) => [
        { label: `Start this: ${f.build}`, kind: "build", minutes: 60 },
        { label: `Ask ${f.talk} what a normal Tuesday looks like`, kind: "talk", minutes: 20 },
        { label: "Note what you lost track of time doing", kind: "reflect", minutes: 10 },
      ],
    },
    {
      phase: "Step 2 · This term",
      title: (f) => `Learn the basics of ${f.craft}`,
      why: () => "A few real hours a week beats a whole holiday of good intentions.",
      actions: (f) => [
        { label: `Learn ${f.learn}`, kind: "learn", minutes: 180 },
        { label: "Find the club, society or online group that already does it", kind: "talk", minutes: 30 },
        { label: "Keep one page of what you tried and what happened", kind: "reflect", minutes: 15 },
      ],
    },
    {
      phase: "Step 3 · This year",
      title: () => "Make something you can show",
      why: () => "Applications, interviews and scholarships all ask the same question: what have you actually done?",
      actions: (f) => [
        { label: `Put together ${f.proof}`, kind: "build", minutes: 240 },
        { label: "Get one adult who isn't family to give feedback", kind: "talk", minutes: 30 },
        { label: "Check which subjects the cheapest route in really needs", kind: "learn", minutes: 45 },
      ],
    },
    {
      phase: "Step 4 · Before you leave school",
      title: () => "Map your routes in — all of them",
      why: () => "There is almost always more than one door: degree, diploma, apprenticeship, portfolio.",
      actions: () => [
        { label: "List three routes into the same work, with costs", kind: "learn", minutes: 60 },
        { label: "Talk to someone who took the route you'd rule out", kind: "talk", minutes: 25 },
        { label: "Pick a first choice and a real backup", kind: "reflect", minutes: 20 },
      ],
    },
    {
      phase: "Step 5 · Keep checking",
      title: () => "Re-check yourself every six months",
      why: () => "You are fifteen to eighteen. Changing your mind with evidence is not failure — it is the point.",
      actions: () => [
        { label: "Re-take the walk and see what moved", kind: "reflect", minutes: 15 },
        { label: "Drop one activity that stopped teaching you anything", kind: "reflect", minutes: 10 },
        { label: "Add one new thing you've never tried", kind: "build", minutes: 60 },
      ],
    },
  ],
  "12to14": [
    {
      phase: "Step 1 · This week",
      title: (f) => `Try one thing — ${f.verb}`,
      why: () => "You don't have to decide anything. You only have to find out what you enjoy.",
      actions: (f) => [
        { label: `Have a go at this: ${f.build}`, kind: "build", minutes: 45 },
        { label: "Tell one person at home what you tried", kind: "talk", minutes: 10 },
        { label: "Was it fun, boring, or hard-but-good?", kind: "reflect", minutes: 5 },
      ],
    },
    {
      phase: "Step 2 · This month",
      title: (f) => `Get a bit better at ${f.craft}`,
      why: () => "Getting better at something is its own kind of fun. Give it four weeks.",
      actions: (f) => [
        { label: `Learn ${f.learn}`, kind: "learn", minutes: 90 },
        { label: "Find a club or a friend who likes it too", kind: "talk", minutes: 20 },
        { label: "Keep the stuff you make in one place", kind: "build", minutes: 15 },
      ],
    },
    {
      phase: "Step 3 · This term",
      title: () => "Meet someone who does it for real",
      why: () => "Career Worlds get a lot less mysterious once you've met one person from inside them.",
      actions: (f) => [
        { label: `Ask a teacher or family friend to introduce you to ${f.talk}`, kind: "talk", minutes: 20 },
        { label: "Write down three questions before you meet", kind: "reflect", minutes: 10 },
        { label: "Watch one day-in-the-life video and compare", kind: "learn", minutes: 15 },
      ],
    },
    {
      phase: "Step 4 · This year",
      title: () => "Explore two other Worlds",
      why: () => "Liking one thing doesn't rule out the rest. Wide first, deep later.",
      actions: () => [
        { label: "Pick two Career Worlds you know nothing about", kind: "learn", minutes: 30 },
        { label: "Try one small thing from each", kind: "build", minutes: 60 },
        { label: "Notice which one you'd do again on a Saturday", kind: "reflect", minutes: 10 },
      ],
    },
    {
      phase: "Step 5 · Keep going",
      title: () => "Come back and check",
      why: () => "You'll change a lot in a year. Your Worlds are allowed to change with you.",
      actions: () => [
        { label: "Re-take the walk next term", kind: "reflect", minutes: 15 },
        { label: "Show someone your collection of stuff you made", kind: "talk", minutes: 15 },
        { label: "Set one small goal for next term", kind: "reflect", minutes: 10 },
      ],
    },
  ],
};

const LEARNING_SPINE: Record<Band, Phase[]> = {
  "18plus": [
    {
      phase: "Track 1 · Foundations",
      title: (f) => `The basics of ${f.craft}`,
      why: () => "Skip this and everything after takes twice as long.",
      actions: (f) => [
        { label: `Learn ${f.learn}`, kind: "learn", minutes: 240 },
        { label: "Practise for twenty minutes, five days a week", kind: "build", minutes: 20 },
      ],
    },
    {
      phase: "Track 2 · Depth",
      title: () => "One thing, properly",
      why: () => "Depth is what people pay for. Breadth is what makes depth findable.",
      actions: (f) => [
        { label: `Go deeper: ${f.deepen}`, kind: "learn", minutes: 400 },
        { label: "Do one project that is slightly too hard", kind: "build", minutes: 480 },
      ],
    },
    {
      phase: "Track 3 · Proof",
      title: () => "Evidence someone else can check",
      why: () => "Certificates help. Work that exists helps more.",
      actions: (f) => [
        { label: `Assemble ${f.proof}`, kind: "build", minutes: 300 },
        { label: "Have it reviewed by someone in the field", kind: "talk", minutes: 45 },
      ],
    },
    {
      phase: "Track 4 · Human skills",
      title: () => "The skills that travel everywhere",
      why: () => "Communication, follow-through and judgement outlast any tool you learn.",
      actions: () => [
        { label: "Explain your work to someone outside the field", kind: "talk", minutes: 30 },
        { label: "Write a weekly review — what worked, what didn't", kind: "reflect", minutes: 15 },
      ],
    },
  ],
  "15to18": [
    {
      phase: "Track 1 · Foundations",
      title: (f) => `Where ${f.craft} starts`,
      why: () => "Small, regular practice while school is still your main job.",
      actions: (f) => [
        { label: `Learn ${f.learn}`, kind: "learn", minutes: 150 },
        { label: "Fifteen minutes a day, four days a week", kind: "build", minutes: 15 },
      ],
    },
    {
      phase: "Track 2 · School, on purpose",
      title: () => "Line up your subjects with the cheapest route in",
      why: () => "Some doors need a specific subject. Most need fewer than people think.",
      actions: () => [
        { label: "Check entry requirements for three routes", kind: "learn", minutes: 45 },
        { label: "Ask a teacher which of your subjects carries most weight", kind: "talk", minutes: 15 },
      ],
    },
    {
      phase: "Track 3 · Proof",
      title: () => "Build the thing you'll be asked about",
      why: () => "Interviews and applications reward evidence over enthusiasm.",
      actions: (f) => [
        { label: `Make ${f.proof}`, kind: "build", minutes: 240 },
        { label: "Write two sentences on what you learned doing it", kind: "reflect", minutes: 15 },
      ],
    },
    {
      phase: "Track 4 · Human skills",
      title: () => "Speak, write, follow through",
      why: () => "These three decide how far the rest of your skills travel.",
      actions: () => [
        { label: "Present something to a group, even a small one", kind: "talk", minutes: 30 },
        { label: "Finish one thing you'd normally abandon", kind: "build", minutes: 60 },
      ],
    },
  ],
  "12to14": [
    {
      phase: "Track 1 · Try it",
      title: (f) => `A first taste of ${f.craft}`,
      why: () => "Learning something new is meant to feel a bit clumsy at first.",
      actions: (f) => [
        { label: `Learn ${f.learn}`, kind: "learn", minutes: 60 },
        { label: "Ten minutes a day is plenty", kind: "build", minutes: 10 },
      ],
    },
    {
      phase: "Track 2 · Keep it up",
      title: () => "Practise a little, often",
      why: () => "Four short goes beat one long one. That's true for everyone.",
      actions: () => [
        { label: "Pick a regular day and time", kind: "reflect", minutes: 5 },
        { label: "Make one small thing each week", kind: "build", minutes: 30 },
      ],
    },
    {
      phase: "Track 3 · Show someone",
      title: () => "Share what you made",
      why: () => "Showing your work is how you find people who like the same things.",
      actions: (f) => [
        { label: `Collect ${f.proof}`, kind: "build", minutes: 45 },
        { label: "Show a teacher, a friend or someone at home", kind: "talk", minutes: 15 },
      ],
    },
    {
      phase: "Track 4 · School skills that help",
      title: () => "Reading, explaining, sticking with it",
      why: () => "These three make every World easier to enter later.",
      actions: () => [
        { label: "Explain something you learned to someone else", kind: "talk", minutes: 15 },
        { label: "Finish one thing you nearly gave up on", kind: "build", minutes: 30 },
      ],
    },
  ],
};

const INTRO: Record<RoadmapKind, Record<Band, string>> = {
  journey: {
    "18plus": "Five steps, roughly two years. Nothing here needs money you don't have.",
    "15to18": "Five steps that fit around school. Try, then choose — not the other way round.",
    "12to14": "Five small steps. You're exploring, not deciding.",
  },
  learning: {
    "18plus": "Four tracks. Do them in order — foundations really do come first.",
    "15to18": "Four tracks that fit alongside your subjects.",
    "12to14": "Four gentle tracks. Little and often.",
  },
};

/** Is this string one of the canonical eight? */
export function asArchetype(v: string | null | undefined): Archetype | null {
  if (!v) return null;
  const hit = ARCHETYPES.find((a) => a.toLowerCase() === v.trim().toLowerCase());
  return hit ?? null;
}

export function buildRoadmap(kind: RoadmapKind, archetype: Archetype, band: Band): Roadmap {
  const f = FLAVOUR[archetype];
  const spine = (kind === "journey" ? JOURNEY_SPINE : LEARNING_SPINE)[band];
  return {
    kind,
    archetype,
    band,
    title: kind === "journey" ? `Your ${archetype} journey` : `Your ${archetype} learning plan`,
    intro: INTRO[kind][band],
    milestones: spine.map((p, i) => ({
      id: `${kind}-${archetype}-${band}-${i + 1}`,
      phase: p.phase,
      title: p.title(f),
      why: p.why(f),
      actions: p.actions(f),
    })),
  };
}

export const ACTION_LABEL: Record<ActionKind, string> = {
  learn: "Learn",
  build: "Make",
  talk: "Talk to someone",
  reflect: "Reflect",
};
